import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Theme } from '@meitu/saas-ui';
import { IBatchTemplateData } from '@meitu/saas-editor-core/es/version-types';
import { IEditorType } from '@/types';

const { AppTheme, Helper } = Theme;

export interface IEditorModel {
    /** 当前编辑器类型 */
    editorType?: IEditorType;
    setEditorType: (payload: IEditorType) => void;
    /** 是否是背景操作 */
    isBackgroundOperation: boolean;
    setIsBackgroundOperation: (payload: boolean) => void;
    getIsBackgroundOperation: () => boolean;
    /** 当前使用的功能 */
    feature: string;
    setFeature: (payload: string) => void;
    getFeature: () => string;
    batchTemplateData: IBatchTemplateData | null;
    setBatchTemplateData: (payload: IBatchTemplateData | null) => void;
    /** 是否显示右侧面板 */
    isShowPanel: boolean;
    setIsShowPanel: (payload: boolean) => void;
    /** 是否显示左侧面板 */
    isShowCollapse: boolean;
    setIsShowCollapse: (payload: boolean) => void;
    getIsShowCollapse: () => boolean;
    /** 全屏 */
    isFullscreen: boolean;
    setIsFullscreen: (payload: boolean) => void;
    /** 主题 */
    theme: string;
    setWhiteTheme: () => void;
    setDarkTheme: () => void;
    setXDesignTheme: () => void;
    /** 禁用应用 */
    isDisable: boolean;
    setIsDisable: (payload: boolean) => void;
    /** 是否锁定面板 */
    isLock: boolean;
    setIsLock: (payload: boolean) => void;
    /** 禁用应用且显示面板 */
    isDisableAppAndShowPanel: boolean;
    setIsDisableAppAndShowPanel: (payload: boolean) => void;
    /** 是否显示拼写错误 */
    isShowTypo: boolean;
    setIsShowTypo: (payload: boolean) => void;
}

const useEditorModel = create<IEditorModel>()(
    immer((set, get) => ({
        editorType: undefined,
        setEditorType: (payload) =>
            set((state) => {
                state.editorType = payload;
            }),
        isBackgroundOperation: false,
        setIsBackgroundOperation: (payload) =>
            set((state) => {
                state.isBackgroundOperation = payload;
            }),
        getIsBackgroundOperation: () => get().isBackgroundOperation,
        feature: '',
        setFeature: (payload) =>
            set((state) => {
                state.feature = payload;
            }),
        getFeature: () => get().feature,
        batchTemplateData: null,
        setBatchTemplateData: (payload) =>
            set((state) => {
                state.batchTemplateData = payload;
            }),
        isShowPanel: true,
        setIsShowPanel: (payload) =>
            set((state) => {
                state.isShowPanel = payload;
            }),
        isShowCollapse: true,
        setIsShowCollapse: (payload) =>
            set((state) => {
                state.isShowCollapse = payload;
            }),
        getIsShowCollapse: () => get().isShowCollapse,
        isFullscreen: false,
        setIsFullscreen: (payload) =>
            set((state) => {
                state.isFullscreen = payload;
            }),
        theme: Helper.getTheme(),
        setWhiteTheme: () =>
            set((state) => {
                state.theme = AppTheme.white;
                Helper.setTheme(AppTheme.white);
            }),
        setDarkTheme: () =>
            set((state) => {
                state.theme = AppTheme.dark;
                Helper.setTheme(AppTheme.dark);
            }),
        setXDesignTheme: () =>
            set((state) => {
                state.theme = AppTheme.xdesign;
                Helper.setTheme(AppTheme.xdesign);
            }),
        isDisable: false,
        setIsDisable: (payload) =>
            set((state) => {
                state.isDisable = payload;
            }),
        isLock: false,
        setIsLock: (payload) =>
            set((state) => {
                state.isLock = payload;
            }),
        isDisableAppAndShowPanel: false,
        setIsDisableAppAndShowPanel: (payload) =>
            set((state) => {
                state.isDisableAppAndShowPanel = payload;
            }),
        isShowTypo: true,
        setIsShowTypo: (payload) =>
            set((state) => {
                state.isShowTypo = payload;
            }),
    })),
);

export { useEditorModel };

/**
 * 获取编辑器界面状态
 *
 * @returns 当前编辑器 Store 状态
 */
export const getEditorModel = () => useEditorModel.getState();
